/**
 * Authentication context — single source of truth for "who is logged in?".
 *
 * Architecture:
 * - A single TanStack Query fetches /api/auth/me on mount and on focus.
 * - The query result is exposed via AuthContext.
 * - Route guards read this context to redirect.
 * - Login/register mutations use React Query's invalidation to trigger a refetch.
 *
 * On status codes:
 * - 401: no session → unauthenticated
 * - 403: session without MFA verification → treated as "needs_mfa". The
 *   backend's /me is gated on requireMfa, so a valid pre-MFA session cannot
 *   read it; we infer pre-MFA state from the 403.
 * - 5xx / network: error state
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import type { MeResponse } from "@sendsms/shared";
import { api, ApiError } from "../api";

// -----------------------------------------------------------
// Query key
// -----------------------------------------------------------

export const meQueryKey = ["auth", "me"] as const;

// -----------------------------------------------------------
// Derived auth status
// -----------------------------------------------------------

export type AuthStatus =
  /** Initial load, don't render protected routes yet. */
  | "loading"
  /** /me returned 401 — no session or session invalid. */
  | "unauthenticated"
  /** Session exists but MFA not verified (401/403 on /me but cookie present). */
  | "needs_mfa"
  /** Fully authenticated; can access app. */
  | "authenticated"
  /** Network or server error — keep last state, show toast. */
  | "error";

type AuthContextValue = {
  status: AuthStatus;
  user: MeResponse["user"] | null;
  organization: MeResponse["organization"] | null;
  session: MeResponse["session"] | null;
  /** LAU F2b: impersonation state for the permanent banner; null normally. */
  impersonation: MeResponse["impersonation"] | null;
  /** 2b-2: true while a support session is live on THIS account. */
  supportAccessActive: boolean;
  error: Error | null;
  /** Manually trigger a /me refetch — used after login/logout/etc. */
  refetch: () => Promise<unknown>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

// -----------------------------------------------------------
// Provider
// -----------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const meQuery = useQuery<MeResponse, Error>({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => api.me({ signal }),
    // Don't throw/retry on auth errors — they're expected
    retry: (_failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) return false;
      }
      return false;
    },
    refetchOnWindowFocus: "always",
    staleTime: 30_000,
  });

  const value = useMemo<AuthContextValue>(() => {
    const status = deriveStatus(meQuery);
    return {
      status,
      user: meQuery.data?.user ?? null,
      organization: meQuery.data?.organization ?? null,
      session: meQuery.data?.session ?? null,
      impersonation: meQuery.data?.impersonation ?? null,
      supportAccessActive: meQuery.data?.support_access_active ?? false,
      // Don't surface auth errors (401/403) to UI as "error" — they're normal
      error:
        meQuery.error instanceof ApiError &&
        (meQuery.error.status === 401 || meQuery.error.status === 403)
          ? null
          : (meQuery.error ?? null),
      refetch: () => meQuery.refetch(),
    };
  }, [meQuery]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// -----------------------------------------------------------
// Hook
// -----------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

// -----------------------------------------------------------
// Helpers
// -----------------------------------------------------------

function deriveStatus(q: {
  isPending: boolean;
  isError: boolean;
  data?: MeResponse;
  error: Error | null;
}): AuthStatus {
  if (q.isPending) return "loading";

  if (q.isError) {
    if (q.error instanceof ApiError) {
      if (q.error.status === 401) return "unauthenticated";
      // 403 from /me = session exists but MFA not verified
      if (q.error.status === 403) return "needs_mfa";
    }
    return "error";
  }

  if (!q.data) return "unauthenticated";

  // Defensive — /me is gated by requireMfa on the server, but we check anyway.
  if (q.data.session.mfa_verified === false) return "needs_mfa";

  return "authenticated";
}
